import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
type MembershipType = 'individu' | 'organisasi' | 'badan_usaha' | 'mitra';
export function RegisterStep2() {
  const navigate = useNavigate();
  const [membershipType, setMembershipType] = useState<MembershipType | null>(null);
  // Form data untuk Anggota Individu
  const [individuData, setIndividuData] = useState({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    occupation: '',
    company: '',
    skills: [] as string[],
    bio: ''
  });
  // Form data untuk Anggota Organisasi
  const [organisasiData, setOrganisasiData] = useState({
    organizationName: '',
    organizationType: '',
    establishedYear: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    picName: '',
    picPosition: '',
    picEmail: '',
    picPhone: '',
    memberCount: '',
    description: '',
    focusArea: [] as string[]
  });
  // Form data untuk Anggota Badan Usaha
  const [badanUsahaData, setBadanUsahaData] = useState({
    companyName: '',
    companyType: '',
    businessScale: '',
    npwp: '',
    establishedYear: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    picName: '',
    picPosition: '',
    picEmail: '',
    picPhone: '',
    employeeCount: '',
    description: '',
    businessSector: [] as string[]
  });
  // Form data untuk Mitra Asosiasi
  const [mitraData, setMitraData] = useState({
    organizationName: '',
    organizationType: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    picName: '',
    picPosition: '',
    picEmail: '',
    picPhone: '',
    partnershipType: '',
    partnershipGoals: '',
    proposedPrograms: '',
    description: ''
  });
  const provinces = ['DKI Jakarta', 'Jawa Barat', 'Jawa Tengah', 'Jawa Timur', 'Banten', 'Bali', 'Sumatera Utara', 'Sumatera Selatan', 'Kalimantan Timur', 'Sulawesi Selatan'];
  const skillOptions = ['Backend Development', 'Frontend Development', 'DevOps', 'Data Science', 'Security', 'Mobile Development', 'UI/UX Design', 'Project Management', 'Cloud Computing', 'Machine Learning'];
  const organizationTypes = ['Komunitas/Kelompok Pengguna', 'Organisasi Pendidikan', 'Organisasi Nirlaba/NGO', 'Asosiasi Profesional', 'Lembaga Penelitian', 'Lembaga Pemerintah'];
  const companyTypes = ['PT (Perseroan Terbatas)', 'CV (Commanditaire Vennootschap)', 'Startup/Rintisan', 'UMKM', 'Koperasi'];
  const businessScales = ['Usaha Mikro', 'Usaha Kecil', 'Usaha Menengah', 'Perusahaan Besar'];
  const businessSectors = ['Software Development', 'IT Consulting', 'E-commerce', 'Fintech', 'Edtech', 'Healthtech', 'Cloud Services', 'Cybersecurity', 'AI/ML', 'IoT'];
  const focusAreas = ['Pendidikan & Pelatihan', 'Penelitian & Pengembangan', 'Advokasi Kebijakan', 'Pengembangan Komunitas', 'Open Source', 'Teknologi Pendidikan', 'Digital Transformation', 'Capacity Building'];
  const partnershipTypes = ['Kemitraan Strategis', 'Kemitraan Teknologi', 'Kemitraan Pendidikan', 'Kemitraan Riset', 'Kemitraan CSR'];
  useEffect(() => {
    const savedData = localStorage.getItem('registrationData');
    if (!savedData) {
      navigate('/register/step1');
      return;
    }
    const data = JSON.parse(savedData);
    setMembershipType(data.membershipType);
  }, [navigate]);
  const handleSkillToggle = (skill: string) => {
    setIndividuData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill) ? prev.skills.filter(s => s !== skill) : [...prev.skills, skill]
    }));
  };
  const handleFocusAreaToggle = (area: string) => {
    setOrganisasiData(prev => ({
      ...prev,
      focusArea: prev.focusArea.includes(area) ? prev.focusArea.filter(a => a !== area) : [...prev.focusArea, area]
    }));
  };
  const handleBusinessSectorToggle = (sector: string) => {
    setBadanUsahaData(prev => ({
      ...prev,
      businessSector: prev.businessSector.includes(sector) ? prev.businessSector.filter(s => s !== sector) : [...prev.businessSector, sector]
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let dataToSave: any = {};
    // Validation based on membership type
    if (membershipType === 'individu') {
      if (!individuData.fullName || !individuData.email || !individuData.phone) {
        alert('Mohon lengkapi semua field yang wajib diisi');
        return;
      }
      if (individuData.skills.length === 0) {
        alert('Pilih minimal 1 keahlian');
        return;
      }
      dataToSave = individuData;
    } else if (membershipType === 'organisasi') {
      if (!organisasiData.organizationName || !organisasiData.email || !organisasiData.picName) {
        alert('Mohon lengkapi semua field yang wajib diisi');
        return;
      }
      if (organisasiData.focusArea.length === 0) {
        alert('Pilih minimal 1 fokus area');
        return;
      }
      dataToSave = organisasiData;
    } else if (membershipType === 'badan_usaha') {
      if (!badanUsahaData.companyName || !badanUsahaData.email || !badanUsahaData.npwp) {
        alert('Mohon lengkapi semua field yang wajib diisi');
        return;
      }
      if (badanUsahaData.businessSector.length === 0) {
        alert('Pilih minimal 1 sektor bisnis');
        return;
      }
      dataToSave = badanUsahaData;
    } else if (membershipType === 'mitra') {
      if (!mitraData.organizationName || !mitraData.email || !mitraData.partnershipType) {
        alert('Mohon lengkapi semua field yang wajib diisi');
        return;
      }
      dataToSave = mitraData;
    }
    // Save to localStorage
    const savedData = JSON.parse(localStorage.getItem('registrationData') || '{}');
    localStorage.setItem('registrationData', JSON.stringify({
      ...savedData,
      personalData: dataToSave
    }));
    navigate('/register/step3');
  };
  if (!membershipType) return null;
  return <div className="min-h-screen bg-gradient-to-br from-atios-primary to-blue-700" data-id="element-4526">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-4527">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8" data-id="element-4528">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3" data-id="element-4529">
            {membershipType === 'individu' && 'Data Pribadi'}
            {membershipType === 'organisasi' && 'Data Organisasi'}
            {membershipType === 'badan_usaha' && 'Data Perusahaan'}
            {membershipType === 'mitra' && 'Data Kemitraan'}
          </h1>
        </div>

        {/* Progress Bar */}
        <div className="mb-8 sm:mb-12" data-id="element-4530">
          <div className="flex items-center justify-between mb-2" data-id="element-4531">
            <span className="text-xs sm:text-sm font-medium text-white" data-id="element-4532">
              Langkah 2 dari 5
            </span>
            <span className="text-xs sm:text-sm text-white/80" data-id="element-4533">40%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2" data-id="element-4534">
            <div className="bg-cyan-400 h-2 rounded-full transition-all" style={{
            width: '40%'
          }} data-id="element-4535" />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-id="element-4536">
          {/* FORM ANGGOTA INDIVIDU */}
          {membershipType === 'individu' && <>
              <Card data-id="element-4537">
                <CardHeader data-id="element-4538">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4539">
                    Informasi Dasar
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4540">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-4541">
                    <div data-id="element-4542">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4543">
                        Nama Lengkap <span className="text-error" data-id="element-4544">*</span>
                      </label>
                      <Input placeholder="John Doe" value={individuData.fullName} onChange={e => setIndividuData(prev => ({
                    ...prev,
                    fullName: e.target.value
                  }))} required data-id="element-4545" />
                    </div>
                    <div data-id="element-4546">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4547">
                        Email <span className="text-error" data-id="element-4548">*</span>
                      </label>
                      <Input type="email" placeholder="john@example.com" value={individuData.email} onChange={e => setIndividuData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))} required data-id="element-4549" />
                    </div>
                    <div data-id="element-4550">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4551">
                        Nomor Telepon <span className="text-error" data-id="element-4552">*</span>
                      </label>
                      <Input type="tel" placeholder="+62812345678" value={individuData.phone} onChange={e => setIndividuData(prev => ({
                    ...prev,
                    phone: e.target.value
                  }))} required data-id="element-4553" />
                    </div>
                    <div data-id="element-4554">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4555">
                        Tanggal Lahir <span className="text-error" data-id="element-4556">*</span>
                      </label>
                      <Input type="date" value={individuData.dateOfBirth} onChange={e => setIndividuData(prev => ({
                    ...prev,
                    dateOfBirth: e.target.value
                  }))} required data-id="element-4557" />
                    </div>
                    <div data-id="element-4558">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4559">
                        Pekerjaan
                      </label>
                      <Input placeholder="Software Engineer" value={individuData.occupation} onChange={e => setIndividuData(prev => ({
                    ...prev,
                    occupation: e.target.value
                  }))} data-id="element-4560" />
                    </div>
                    <div data-id="element-4561">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4562">
                        Perusahaan
                      </label>
                      <Input placeholder="PT Tech Indonesia" value={individuData.company} onChange={e => setIndividuData(prev => ({
                    ...prev,
                    company: e.target.value
                  }))} data-id="element-4563" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4564">
                <CardHeader data-id="element-4565">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4566">Alamat</CardTitle>
                </CardHeader>
                <CardContent data-id="element-4567">
                  <div className="grid grid-cols-1 gap-4 sm:gap-6" data-id="element-4568">
                    <div data-id="element-4569">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4570">
                        Alamat <span className="text-error" data-id="element-4571">*</span>
                      </label>
                      <textarea className="w-full min-h-[80px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Jl. Contoh No. 123" value={individuData.address} onChange={e => setIndividuData(prev => ({
                    ...prev,
                    address: e.target.value
                  }))} required data-id="element-4572" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" data-id="element-4573">
                      <div data-id="element-4574">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4575">
                          Kota <span className="text-error" data-id="element-4576">*</span>
                        </label>
                        <Input placeholder="Jakarta" value={individuData.city} onChange={e => setIndividuData(prev => ({
                      ...prev,
                      city: e.target.value
                    }))} required data-id="element-4577" />
                      </div>
                      <div data-id="element-4578">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4579">
                          Provinsi <span className="text-error" data-id="element-4580">*</span>
                        </label>
                        <select className="w-full h-10 px-3 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" value={individuData.province} onChange={e => setIndividuData(prev => ({
                      ...prev,
                      province: e.target.value
                    }))} required data-id="element-4581">
                          <option value="" data-id="element-4582">Pilih Provinsi</option>
                          {provinces.map(prov => <option key={prov} value={prov} data-id="element-4583">
                              {prov}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-4584">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4585">
                          Kode Pos <span className="text-error" data-id="element-4586">*</span>
                        </label>
                        <Input placeholder="12345" value={individuData.postalCode} onChange={e => setIndividuData(prev => ({
                      ...prev,
                      postalCode: e.target.value
                    }))} required data-id="element-4587" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4588">
                <CardHeader data-id="element-4589">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4590">
                    Keahlian
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4591">
                  <div className="space-y-4 sm:space-y-6" data-id="element-4592">
                    <div data-id="element-4593">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-3" data-id="element-4594">
                        Keahlian <span className="text-error" data-id="element-4595">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-4596">
                        {skillOptions.map(skill => <button key={skill} type="button" onClick={() => handleSkillToggle(skill)} className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${individuData.skills.includes(skill) ? 'bg-cyan-500 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`} data-id="element-4597">
                            {skill}
                          </button>)}
                      </div>
                      <p className="text-xs text-neutral-500 mt-2" data-id="element-4598">
                        Pilih minimal 1 keahlian
                      </p>
                    </div>
                    <div data-id="element-4599">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4600">
                        Biografi Singkat
                      </label>
                      <textarea className="w-full min-h-[100px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Ceritakan tentang Anda..." maxLength={500} value={individuData.bio} onChange={e => setIndividuData(prev => ({
                    ...prev,
                    bio: e.target.value
                  }))} data-id="element-4601" />
                      <p className="text-xs text-neutral-500 mt-2" data-id="element-4602">
                        {individuData.bio.length}/500 karakter
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>}

          {/* FORM ANGGOTA ORGANISASI */}
          {membershipType === 'organisasi' && <>
              <Card data-id="element-4603">
                <CardHeader data-id="element-4604">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4605">
                    Informasi Organisasi
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4606">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-4607">
                    <div data-id="element-4608">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4609">
                        Nama Organisasi <span className="text-error" data-id="element-4610">*</span>
                      </label>
                      <Input placeholder="Tech Indonesia Foundation" value={organisasiData.organizationName} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    organizationName: e.target.value
                  }))} required data-id="element-4611" />
                    </div>
                    <div data-id="element-4612">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4613">
                        Jenis Organisasi <span className="text-error" data-id="element-4614">*</span>
                      </label>
                      <select className="w-full h-10 px-3 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" value={organisasiData.organizationType} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    organizationType: e.target.value
                  }))} required data-id="element-4615">
                        <option value="" data-id="element-4616">Pilih Jenis</option>
                        {organizationTypes.map(type => <option key={type} value={type} data-id="element-4617">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-4618">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4619">
                        Tahun Berdiri <span className="text-error" data-id="element-4620">*</span>
                      </label>
                      <Input type="number" placeholder="2020" min="1900" max={new Date().getFullYear()} value={organisasiData.establishedYear} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    establishedYear: e.target.value
                  }))} required data-id="element-4621" />
                    </div>
                    <div data-id="element-4622">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4623">
                        Jumlah Anggota <span className="text-error" data-id="element-4624">*</span>
                      </label>
                      <Input type="number" placeholder="50" value={organisasiData.memberCount} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    memberCount: e.target.value
                  }))} required data-id="element-4625" />
                    </div>
                    <div data-id="element-4626">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4627">
                        Email Organisasi <span className="text-error" data-id="element-4628">*</span>
                      </label>
                      <Input type="email" placeholder="contact@techindonesia.org" value={organisasiData.email} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))} required data-id="element-4629" />
                    </div>
                    <div data-id="element-4630">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4631">
                        Nomor Telepon <span className="text-error" data-id="element-4632">*</span>
                      </label>
                      <Input type="tel" placeholder="+62212345678" value={organisasiData.phone} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    phone: e.target.value
                  }))} required data-id="element-4633" />
                    </div>
                    <div className="md:col-span-2" data-id="element-4634">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4635">
                        Website
                      </label>
                      <Input type="url" placeholder="https://www.techindonesia.org" value={organisasiData.website} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    website: e.target.value
                  }))} data-id="element-4636" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4637">
                <CardHeader data-id="element-4638">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4639">
                    Penanggung Jawab
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4640">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-4641">
                    <div data-id="element-4642">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4643">
                        Nama Lengkap <span className="text-error" data-id="element-4644">*</span>
                      </label>
                      <Input placeholder="Budi Santoso" value={organisasiData.picName} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    picName: e.target.value
                  }))} required data-id="element-4645" />
                    </div>
                    <div data-id="element-4646">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4647">
                        Jabatan <span className="text-error" data-id="element-4648">*</span>
                      </label>
                      <Input placeholder="Ketua/Direktur" value={organisasiData.picPosition} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    picPosition: e.target.value
                  }))} required data-id="element-4649" />
                    </div>
                    <div data-id="element-4650">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4651">
                        Email <span className="text-error" data-id="element-4652">*</span>
                      </label>
                      <Input type="email" placeholder="budi@techindonesia.org" value={organisasiData.picEmail} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    picEmail: e.target.value
                  }))} required data-id="element-4653" />
                    </div>
                    <div data-id="element-4654">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4655">
                        Nomor Telepon <span className="text-error" data-id="element-4656">*</span>
                      </label>
                      <Input type="tel" placeholder="+62812345678" value={organisasiData.picPhone} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    picPhone: e.target.value
                  }))} required data-id="element-4657" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4658">
                <CardHeader data-id="element-4659">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4660">Alamat</CardTitle>
                </CardHeader>
                <CardContent data-id="element-4661">
                  <div className="grid grid-cols-1 gap-4 sm:gap-6" data-id="element-4662">
                    <div data-id="element-4663">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4664">
                        Alamat <span className="text-error" data-id="element-4665">*</span>
                      </label>
                      <textarea className="w-full min-h-[80px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Jl. Contoh No. 123" value={organisasiData.address} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    address: e.target.value
                  }))} required data-id="element-4666" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" data-id="element-4667">
                      <div data-id="element-4668">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4669">
                          Kota <span className="text-error" data-id="element-4670">*</span>
                        </label>
                        <Input placeholder="Jakarta" value={organisasiData.city} onChange={e => setOrganisasiData(prev => ({
                      ...prev,
                      city: e.target.value
                    }))} required data-id="element-4671" />
                      </div>
                      <div data-id="element-4672">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4673">
                          Provinsi <span className="text-error" data-id="element-4674">*</span>
                        </label>
                        <select className="w-full h-10 px-3 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" value={organisasiData.province} onChange={e => setOrganisasiData(prev => ({
                      ...prev,
                      province: e.target.value
                    }))} required data-id="element-4675">
                          <option value="" data-id="element-4676">Pilih Provinsi</option>
                          {provinces.map(prov => <option key={prov} value={prov} data-id="element-4677">
                              {prov}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-4678">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4679">
                          Kode Pos <span className="text-error" data-id="element-4680">*</span>
                        </label>
                        <Input placeholder="12345" value={organisasiData.postalCode} onChange={e => setOrganisasiData(prev => ({
                      ...prev,
                      postalCode: e.target.value
                    }))} required data-id="element-4681" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4682">
                <CardHeader data-id="element-4683">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4684">
                    Fokus Area & Deskripsi
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4685">
                  <div className="space-y-4 sm:space-y-6" data-id="element-4686">
                    <div data-id="element-4687">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-3" data-id="element-4688">
                        Fokus Area <span className="text-error" data-id="element-4689">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-4690">
                        {focusAreas.map(area => <button key={area} type="button" onClick={() => handleFocusAreaToggle(area)} className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${organisasiData.focusArea.includes(area) ? 'bg-cyan-500 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`} data-id="element-4691">
                            {area}
                          </button>)}
                      </div>
                      <p className="text-xs text-neutral-500 mt-2" data-id="element-4692">
                        Pilih minimal 1 fokus area
                      </p>
                    </div>
                    <div data-id="element-4693">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4694">
                        Deskripsi Organisasi{' '}
                        <span className="text-error" data-id="element-4695">*</span>
                      </label>
                      <textarea className="w-full min-h-[100px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Ceritakan tentang organisasi Anda..." maxLength={500} value={organisasiData.description} onChange={e => setOrganisasiData(prev => ({
                    ...prev,
                    description: e.target.value
                  }))} required data-id="element-4696" />
                      <p className="text-xs text-neutral-500 mt-2" data-id="element-4697">
                        {organisasiData.description.length}/500 karakter
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>}

          {/* FORM ANGGOTA BADAN USAHA */}
          {membershipType === 'badan_usaha' && <>
              <Card data-id="element-4698">
                <CardHeader data-id="element-4699">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4700">
                    Informasi Perusahaan
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4701">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-4702">
                    <div data-id="element-4703">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4704">
                        Nama Perusahaan <span className="text-error" data-id="element-4705">*</span>
                      </label>
                      <Input placeholder="PT Digital Solutions" value={badanUsahaData.companyName} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    companyName: e.target.value
                  }))} required data-id="element-4706" />
                    </div>
                    <div data-id="element-4707">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4708">
                        Bentuk Usaha <span className="text-error" data-id="element-4709">*</span>
                      </label>
                      <select className="w-full h-10 px-3 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" value={badanUsahaData.companyType} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    companyType: e.target.value
                  }))} required data-id="element-4710">
                        <option value="" data-id="element-4711">Pilih Bentuk Usaha</option>
                        {companyTypes.map(type => <option key={type} value={type} data-id="element-4712">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-4713">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4714">
                        Skala Usaha <span className="text-error" data-id="element-4715">*</span>
                      </label>
                      <select className="w-full h-10 px-3 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" value={badanUsahaData.businessScale} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    businessScale: e.target.value
                  }))} required data-id="element-4716">
                        <option value="" data-id="element-4717">Pilih Skala Usaha</option>
                        {businessScales.map(scale => <option key={scale} value={scale} data-id="element-4718">
                            {scale}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-4719">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4720">
                        NPWP <span className="text-error" data-id="element-4721">*</span>
                      </label>
                      <Input placeholder="12.345.678.9-012.345" value={badanUsahaData.npwp} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    npwp: e.target.value
                  }))} required data-id="element-4722" />
                    </div>
                    <div data-id="element-4723">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4724">
                        Tahun Berdiri <span className="text-error" data-id="element-4725">*</span>
                      </label>
                      <Input type="number" placeholder="2020" min="1900" max={new Date().getFullYear()} value={badanUsahaData.establishedYear} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    establishedYear: e.target.value
                  }))} required data-id="element-4726" />
                    </div>
                    <div data-id="element-4727">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4728">
                        Jumlah Karyawan <span className="text-error" data-id="element-4729">*</span>
                      </label>
                      <Input type="number" placeholder="25" value={badanUsahaData.employeeCount} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    employeeCount: e.target.value
                  }))} required data-id="element-4730" />
                    </div>
                    <div data-id="element-4731">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4732">
                        Email Perusahaan <span className="text-error" data-id="element-4733">*</span>
                      </label>
                      <Input type="email" placeholder="info@digitalsolutions.id" value={badanUsahaData.email} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))} required data-id="element-4734" />
                    </div>
                    <div data-id="element-4735">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4736">
                        Nomor Telepon <span className="text-error" data-id="element-4737">*</span>
                      </label>
                      <Input type="tel" placeholder="+62212345678" value={badanUsahaData.phone} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    phone: e.target.value
                  }))} required data-id="element-4738" />
                    </div>
                    <div className="md:col-span-2" data-id="element-4739">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4740">
                        Website
                      </label>
                      <Input type="url" placeholder="https://www.digitalsolutions.id" value={badanUsahaData.website} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    website: e.target.value
                  }))} data-id="element-4741" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4742">
                <CardHeader data-id="element-4743">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4744">
                    Penanggung Jawab
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4745">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-4746">
                    <div data-id="element-4747">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4748">
                        Nama Lengkap <span className="text-error" data-id="element-4749">*</span>
                      </label>
                      <Input placeholder="Ahmad Wijaya" value={badanUsahaData.picName} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    picName: e.target.value
                  }))} required data-id="element-4750" />
                    </div>
                    <div data-id="element-4751">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4752">
                        Jabatan <span className="text-error" data-id="element-4753">*</span>
                      </label>
                      <Input placeholder="CEO/Direktur" value={badanUsahaData.picPosition} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    picPosition: e.target.value
                  }))} required data-id="element-4754" />
                    </div>
                    <div data-id="element-4755">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4756">
                        Email <span className="text-error" data-id="element-4757">*</span>
                      </label>
                      <Input type="email" placeholder="ahmad@digitalsolutions.id" value={badanUsahaData.picEmail} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    picEmail: e.target.value
                  }))} required data-id="element-4758" />
                    </div>
                    <div data-id="element-4759">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4760">
                        Nomor Telepon <span className="text-error" data-id="element-4761">*</span>
                      </label>
                      <Input type="tel" placeholder="+62812345678" value={badanUsahaData.picPhone} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    picPhone: e.target.value
                  }))} required data-id="element-4762" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4763">
                <CardHeader data-id="element-4764">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4765">Alamat</CardTitle>
                </CardHeader>
                <CardContent data-id="element-4766">
                  <div className="grid grid-cols-1 gap-4 sm:gap-6" data-id="element-4767">
                    <div data-id="element-4768">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4769">
                        Alamat <span className="text-error" data-id="element-4770">*</span>
                      </label>
                      <textarea className="w-full min-h-[80px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Jl. Contoh No. 123" value={badanUsahaData.address} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    address: e.target.value
                  }))} required data-id="element-4771" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" data-id="element-4772">
                      <div data-id="element-4773">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4774">
                          Kota <span className="text-error" data-id="element-4775">*</span>
                        </label>
                        <Input placeholder="Jakarta" value={badanUsahaData.city} onChange={e => setBadanUsahaData(prev => ({
                      ...prev,
                      city: e.target.value
                    }))} required data-id="element-4776" />
                      </div>
                      <div data-id="element-4777">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4778">
                          Provinsi <span className="text-error" data-id="element-4779">*</span>
                        </label>
                        <select className="w-full h-10 px-3 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" value={badanUsahaData.province} onChange={e => setBadanUsahaData(prev => ({
                      ...prev,
                      province: e.target.value
                    }))} required data-id="element-4780">
                          <option value="" data-id="element-4781">Pilih Provinsi</option>
                          {provinces.map(prov => <option key={prov} value={prov} data-id="element-4782">
                              {prov}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-4783">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4784">
                          Kode Pos <span className="text-error" data-id="element-4785">*</span>
                        </label>
                        <Input placeholder="12345" value={badanUsahaData.postalCode} onChange={e => setBadanUsahaData(prev => ({
                      ...prev,
                      postalCode: e.target.value
                    }))} required data-id="element-4786" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4787">
                <CardHeader data-id="element-4788">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4789">
                    Sektor Bisnis & Deskripsi
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4790">
                  <div className="space-y-4 sm:space-y-6" data-id="element-4791">
                    <div data-id="element-4792">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-3" data-id="element-4793">
                        Sektor Bisnis <span className="text-error" data-id="element-4794">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-4795">
                        {businessSectors.map(sector => <button key={sector} type="button" onClick={() => handleBusinessSectorToggle(sector)} className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${badanUsahaData.businessSector.includes(sector) ? 'bg-cyan-500 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`} data-id="element-4796">
                            {sector}
                          </button>)}
                      </div>
                      <p className="text-xs text-neutral-500 mt-2" data-id="element-4797">
                        Pilih minimal 1 sektor bisnis
                      </p>
                    </div>
                    <div data-id="element-4798">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4799">
                        Deskripsi Perusahaan{' '}
                        <span className="text-error" data-id="element-4800">*</span>
                      </label>
                      <textarea className="w-full min-h-[100px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Ceritakan tentang perusahaan Anda..." maxLength={500} value={badanUsahaData.description} onChange={e => setBadanUsahaData(prev => ({
                    ...prev,
                    description: e.target.value
                  }))} required data-id="element-4801" />
                      <p className="text-xs text-neutral-500 mt-2" data-id="element-4802">
                        {badanUsahaData.description.length}/500 karakter
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>}

          {/* FORM MITRA ASOSIASI */}
          {membershipType === 'mitra' && <>
              <Card data-id="element-4803">
                <CardHeader data-id="element-4804">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4805">
                    Informasi Organisasi Mitra
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4806">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-4807">
                    <div data-id="element-4808">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4809">
                        Nama Organisasi <span className="text-error" data-id="element-4810">*</span>
                      </label>
                      <Input placeholder="Tech Partner Corp" value={mitraData.organizationName} onChange={e => setMitraData(prev => ({
                    ...prev,
                    organizationName: e.target.value
                  }))} required data-id="element-4811" />
                    </div>
                    <div data-id="element-4812">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4813">
                        Jenis Organisasi <span className="text-error" data-id="element-4814">*</span>
                      </label>
                      <Input placeholder="Perusahaan Teknologi" value={mitraData.organizationType} onChange={e => setMitraData(prev => ({
                    ...prev,
                    organizationType: e.target.value
                  }))} required data-id="element-4815" />
                    </div>
                    <div data-id="element-4816">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4817">
                        Email <span className="text-error" data-id="element-4818">*</span>
                      </label>
                      <Input type="email" placeholder="partner@techcorp.com" value={mitraData.email} onChange={e => setMitraData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))} required data-id="element-4819" />
                    </div>
                    <div data-id="element-4820">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4821">
                        Nomor Telepon <span className="text-error" data-id="element-4822">*</span>
                      </label>
                      <Input type="tel" placeholder="+62212345678" value={mitraData.phone} onChange={e => setMitraData(prev => ({
                    ...prev,
                    phone: e.target.value
                  }))} required data-id="element-4823" />
                    </div>
                    <div className="md:col-span-2" data-id="element-4824">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4825">
                        Website
                      </label>
                      <Input type="url" placeholder="https://www.techcorp.com" value={mitraData.website} onChange={e => setMitraData(prev => ({
                    ...prev,
                    website: e.target.value
                  }))} data-id="element-4826" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4827">
                <CardHeader data-id="element-4828">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4829">
                    Penanggung Jawab
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4830">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-4831">
                    <div data-id="element-4832">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4833">
                        Nama Lengkap <span className="text-error" data-id="element-4834">*</span>
                      </label>
                      <Input placeholder="Siti Nurhaliza" value={mitraData.picName} onChange={e => setMitraData(prev => ({
                    ...prev,
                    picName: e.target.value
                  }))} required data-id="element-4835" />
                    </div>
                    <div data-id="element-4836">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4837">
                        Jabatan <span className="text-error" data-id="element-4838">*</span>
                      </label>
                      <Input placeholder="Partnership Manager" value={mitraData.picPosition} onChange={e => setMitraData(prev => ({
                    ...prev,
                    picPosition: e.target.value
                  }))} required data-id="element-4839" />
                    </div>
                    <div data-id="element-4840">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4841">
                        Email <span className="text-error" data-id="element-4842">*</span>
                      </label>
                      <Input type="email" placeholder="siti@techcorp.com" value={mitraData.picEmail} onChange={e => setMitraData(prev => ({
                    ...prev,
                    picEmail: e.target.value
                  }))} required data-id="element-4843" />
                    </div>
                    <div data-id="element-4844">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4845">
                        Nomor Telepon <span className="text-error" data-id="element-4846">*</span>
                      </label>
                      <Input type="tel" placeholder="+62812345678" value={mitraData.picPhone} onChange={e => setMitraData(prev => ({
                    ...prev,
                    picPhone: e.target.value
                  }))} required data-id="element-4847" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4848">
                <CardHeader data-id="element-4849">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4850">Alamat</CardTitle>
                </CardHeader>
                <CardContent data-id="element-4851">
                  <div className="grid grid-cols-1 gap-4 sm:gap-6" data-id="element-4852">
                    <div data-id="element-4853">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4854">
                        Alamat <span className="text-error" data-id="element-4855">*</span>
                      </label>
                      <textarea className="w-full min-h-[80px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Jl. Contoh No. 123" value={mitraData.address} onChange={e => setMitraData(prev => ({
                    ...prev,
                    address: e.target.value
                  }))} required data-id="element-4856" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" data-id="element-4857">
                      <div data-id="element-4858">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4859">
                          Kota <span className="text-error" data-id="element-4860">*</span>
                        </label>
                        <Input placeholder="Jakarta" value={mitraData.city} onChange={e => setMitraData(prev => ({
                      ...prev,
                      city: e.target.value
                    }))} required data-id="element-4861" />
                      </div>
                      <div data-id="element-4862">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4863">
                          Provinsi <span className="text-error" data-id="element-4864">*</span>
                        </label>
                        <select className="w-full h-10 px-3 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" value={mitraData.province} onChange={e => setMitraData(prev => ({
                      ...prev,
                      province: e.target.value
                    }))} required data-id="element-4865">
                          <option value="" data-id="element-4866">Pilih Provinsi</option>
                          {provinces.map(prov => <option key={prov} value={prov} data-id="element-4867">
                              {prov}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-4868">
                        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4869">
                          Kode Pos <span className="text-error" data-id="element-4870">*</span>
                        </label>
                        <Input placeholder="12345" value={mitraData.postalCode} onChange={e => setMitraData(prev => ({
                      ...prev,
                      postalCode: e.target.value
                    }))} required data-id="element-4871" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-4872">
                <CardHeader data-id="element-4873">
                  <CardTitle className="text-base sm:text-lg" data-id="element-4874">
                    Detail Kemitraan
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-4875">
                  <div className="space-y-4 sm:space-y-6" data-id="element-4876">
                    <div data-id="element-4877">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4878">
                        Jenis Kemitraan <span className="text-error" data-id="element-4879">*</span>
                      </label>
                      <select className="w-full h-10 px-3 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" value={mitraData.partnershipType} onChange={e => setMitraData(prev => ({
                    ...prev,
                    partnershipType: e.target.value
                  }))} required data-id="element-4880">
                        <option value="" data-id="element-4881">Pilih Jenis Kemitraan</option>
                        {partnershipTypes.map(type => <option key={type} value={type} data-id="element-4882">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-4883">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4884">
                        Tujuan Kemitraan <span className="text-error" data-id="element-4885">*</span>
                      </label>
                      <textarea className="w-full min-h-[80px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Jelaskan tujuan kemitraan dengan ATIOS..." maxLength={500} value={mitraData.partnershipGoals} onChange={e => setMitraData(prev => ({
                    ...prev,
                    partnershipGoals: e.target.value
                  }))} required data-id="element-4886" />
                      <p className="text-xs text-neutral-500 mt-2" data-id="element-4887">
                        {mitraData.partnershipGoals.length}/500 karakter
                      </p>
                    </div>
                    <div data-id="element-4888">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4889">
                        Program yang Diusulkan{' '}
                        <span className="text-error" data-id="element-4890">*</span>
                      </label>
                      <textarea className="w-full min-h-[80px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Jelaskan program kolaborasi yang diusulkan..." maxLength={500} value={mitraData.proposedPrograms} onChange={e => setMitraData(prev => ({
                    ...prev,
                    proposedPrograms: e.target.value
                  }))} required data-id="element-4891" />
                      <p className="text-xs text-neutral-500 mt-2" data-id="element-4892">
                        {mitraData.proposedPrograms.length}/500 karakter
                      </p>
                    </div>
                    <div data-id="element-4893">
                      <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-2" data-id="element-4894">
                        Deskripsi Organisasi
                      </label>
                      <textarea className="w-full min-h-[80px] px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atios-primary" placeholder="Ceritakan tentang organisasi Anda..." maxLength={500} value={mitraData.description} onChange={e => setMitraData(prev => ({
                    ...prev,
                    description: e.target.value
                  }))} data-id="element-4895" />
                      <p className="text-xs text-neutral-500 mt-2" data-id="element-4896">
                        {mitraData.description.length}/500 karakter
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>}

          {/* Action Buttons */}
          <div className="flex gap-3 sm:gap-4" data-id="element-4897">
            <Button type="button" variant="secondary" onClick={() => navigate('/register/step1')} className="flex-1" size="lg" data-id="element-4898">
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" data-id="element-4899" />
              Kembali
            </Button>
            <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600" size="lg" data-id="element-4900">
              Lanjutkan
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" data-id="element-4901" />
            </Button>
          </div>
        </form>
      </div>
    </div>;
}